import { action, makeObservable, observable } from 'mobx';

import { DEFAULT_TIMEZONE } from 'core/constants';

import Logger from 'utils/logger';

const renderableZones = new Map<string, boolean>();

export const deviceTimezone = (): string | null => Intl.DateTimeFormat().resolvedOptions().timeZone || null;

export const isRenderableZone = (zone: string | null | undefined): zone is string => {
  if (!zone) return false;
  let renderable = renderableZones.get(zone);
  if (renderable === undefined) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: zone });
      renderable = true;
    } catch {
      renderable = false;
      Logger.warn(`Timezone "${zone}" cannot be rendered by this browser`);
    }
    renderableZones.set(zone, renderable);
  }
  return renderable;
};

type SessionTimezone = { readerTimezone?: string | null; timezoneModelEnabled?: boolean };

export class TimezoneStore {
  @observable enabled = false;

  @observable private serverZone: string | null = null;

  @observable private active = false;

  private issued = 0;

  private lastSequence = 0;

  constructor() {
    makeObservable(this);
  }

  get readerZone(): string {
    if (this.serverZone) return this.serverZone;
    const device = deviceTimezone();
    return isRenderableZone(device) ? device : DEFAULT_TIMEZONE;
  }

  nextSequence(): number {
    this.issued += 1;
    return this.issued;
  }

  @action startSession({ readerTimezone, timezoneModelEnabled }: SessionTimezone): void {
    this.active = true;
    this.enabled = timezoneModelEnabled === true;
    this.serverZone = isRenderableZone(readerTimezone) ? readerTimezone : null;
  }

  @action applyReaderTimezone(zone: string, sequence: number): void {
    if (!this.active || sequence <= this.lastSequence || !isRenderableZone(zone)) return;
    this.serverZone = zone;
    this.lastSequence = sequence;
  }

  @action confirmZone(zone: string | null | undefined): void {
    if (!this.active) return;
    if (isRenderableZone(zone)) this.serverZone = zone;
    this.lastSequence = this.issued;
  }

  @action applyPublicFlag(enabled: boolean | undefined): void {
    if (!this.active) this.enabled = enabled === true;
  }

  @action clearPublicFlag(): void {
    if (!this.active) this.enabled = false;
  }

  @action reset(): void {
    this.active = false;
    this.enabled = false;
    this.serverZone = null;
    this.lastSequence = this.issued;
  }
}

export const timezoneStore = new TimezoneStore();
